import React from "react";
import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import { Reveal } from "@/lib/motion";
import { Page, PageHero, Overline, AppointmentForm } from "@/components/shared";
import { useContent, buildWa } from "@/lib/ContentProvider";

export default function Contact() {
  const { siteConfig } = useContent();
  const INFO = [
    { icon: MapPin, label: "Address", value: siteConfig.address, href: `https://maps.google.com/?q=${encodeURIComponent(siteConfig.address || "Kathgodam Haldwani")}` },
    { icon: Phone, label: "Phone", value: siteConfig.phone, href: `tel:${siteConfig.phoneRaw}` },
    { icon: Mail, label: "Email", value: siteConfig.email, href: `mailto:${siteConfig.email}` },
    ...(siteConfig.hours ? [{ icon: Clock, label: "Hours", value: siteConfig.hours }] : []),
  ];
  return (
    <Page testid="contact-page">
      <PageHero
        overline="Contact Us"
        titleLines={["Let's talk about", <span key="2">your <span className="italic text-alch-terracotta">recovery</span></span>]}
        subtitle="Reach us on WhatsApp, phone, or visit us in Kathgodam, Haldwani."
      />

      <section className="px-5 py-24 md:px-8 md:py-32">
        <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <Overline>Get in touch</Overline>
            <div className="mt-8 space-y-5">
              {INFO.map((c) => (
                <Reveal key={c.label}>
                  <a href={c.href || undefined} target={c.href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
                    className="flex items-start gap-4 rounded-2xl border border-alch-forest/10 bg-alch-surface p-5 transition-colors hover:bg-alch-sand">
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-alch-forest text-alch-bg">
                      <c.icon className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />
                    </span>
                    <span>
                      <span className="block text-xs uppercase tracking-[0.14em] text-alch-muted">{c.label}</span>
                      <span className="mt-1 block text-alch-ink/85">{c.value}</span>
                    </span>
                  </a>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.1}>
              <a href={buildWa(siteConfig.whatsapp)} target="_blank" rel="noreferrer" data-testid="contact-whatsapp"
                className="mt-6 flex items-center justify-center gap-2 rounded-full bg-alch-terracotta px-8 py-4 text-sm font-medium text-white transition-transform hover:scale-[1.03]">
                <MessageCircle className="h-4 w-4" /> Chat with us on WhatsApp
              </a>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-6 overflow-hidden rounded-[1.5rem] border border-alch-forest/10">
                <iframe
                  title="ALCH location map"
                  src={`https://www.google.com/maps?q=${encodeURIComponent(siteConfig.address || "Kathgodam Haldwani Uttarakhand")}&output=embed`}
                  className="h-64 w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </Reveal>
          </div>

          <div>
            <Overline>Book an appointment</Overline>
            <p className="mt-4 mb-6 text-alch-ink/70">
              Fill in a few details and we&rsquo;ll open WhatsApp with your request ready to send.
            </p>
            <AppointmentForm />
          </div>
        </div>
      </section>
    </Page>
  );
}
